import React, {useEffect, useState} from "react";
import localStyles from "./index.module.css";
import commonStyles from '../common/commonStyles/table.module.css';
import {Dept, Doctor} from "../dashboard";
import {Button, Form, Header, Icon, Loader, Menu, Message, Modal, Table} from "semantic-ui-react";
import agent, {PaginateOptions} from "../../agent";
import {Link} from "react-router-dom";
import {DoctorTitleCase, GeneralTitleCase, getDoctorTitle, getGeneralTitle,} from "../../utils/common";
import {hasRole, UserRole} from "../../helpers/UserRole";
import {Utils} from "../../helpers/Utils";
import {firstLetterToUpperCase} from "../workTablePatients/PatientsList";
import ConfirmDeleteModal from "../common/ConfirmDeleteModal";
import TablePage from "../general/TablePage";
import CreatedTaskDetailsModal from "../workTablePatients/CreatedTaskDetailsModal";
import {useUrlPagination} from "../../hooks/useUrlPagination";
import {
    CommonListPagePaginationResponse,
    DEFAULT_LIMIT,
    DEFAULT_PAGE,
    handleSortPagination,
    ListState,
    ObjectReference,
} from "../../helpers/TablePageUtils";
import {textLabels} from "../../consts/textLabels";
import {DeptPaginationResponse} from "../workTableDepts/DeptList";

export interface DoctorPaginationResponse extends CommonListPagePaginationResponse<Doctor> {}

const initialState: ListState<Doctor> = {
    page: DEFAULT_PAGE,
    limit: DEFAULT_LIMIT,
    sortColumn: "surname",
    searchValue: "",
    docs: [],
    totalDocs: 0,
    totalPages: 1,
    loading: false,
    error: null,
};

const DoctorsList: React.FC = () => {
    const currentPath = "/dashboard/doctors";

    const { state, setState, updateUrl } = useUrlPagination<ListState<Doctor>>(initialState, {
        defaultSortColumn: "surname",
    });

    const [searchInput, setSearchInput] = useState(state.searchValue);
    const [removeObject, setRemoveObject] = useState<ObjectReference | null>(null);
    const [createdTask, setCreatedTask] = useState<any>(undefined);
    const [depts, setDepts] = useState<Dept[]>([]);
    const [loadingDepts, setLoadingDepts] = useState(false);

    const loadDepts = async () => {
        setLoadingDepts(true);
        try {
            const response: DeptPaginationResponse = await agent.DeptProvider.getList();
            setDepts(response.docs);
        } catch (error) {
            console.error(`Ошибка загрузки ${getGeneralTitle(GeneralTitleCase.departmantov)}:`, error);
        } finally {
            setLoadingDepts(false);
        }
    };

    const fetchList = async () => {
        setState(prev => ({ ...prev, loading: true, error: null }));

        const paginateOptions: PaginateOptions = {
            page: state.page,
            limit: state.limit,
            sort: state.sortColumn,
            search: state.searchValue,
        };

        try {
            const response: DoctorPaginationResponse = await agent.DoctorProvider.getList(paginateOptions);

            if (response) {
                setState(prev => ({
                    ...prev,
                    docs: response.docs,
                    totalDocs: response.totalDocs,
                    totalPages: response.totalPages,
                    loading: false,
                    error: null,
                }));
            }
        } catch (error) {
            console.error(`Ошибка получения списка ${getDoctorTitle(DoctorTitleCase.ov)}:`, error);
            setState(prev => ({
                ...prev,
                docs: [],
                totalDocs: 0,
                totalPages: 1,
                loading: false,
                error: `Не удалось загрузить список ${getDoctorTitle(DoctorTitleCase.ov)}. Пожалуйста, попробуйте позже.`,
            }));
        }
    };

    useEffect(() => {
        loadDepts();
    }, []);

    useEffect(() => {
        fetchList();
    }, [state.page, state.limit, state.sortColumn, state.searchValue]);

    useEffect(() => {
        updateUrl();
    }, [state.page, state.limit, state.sortColumn, state.searchValue]);

    const handleSearch = () => {
        setState(prev => ({ ...prev, searchValue: searchInput, page: DEFAULT_PAGE }));
    };

    const handleClearSearch = () => {
        setSearchInput("");
        setState(prev => ({ ...prev, searchValue: "", page: DEFAULT_PAGE }));
    };

    const handleSort = (column: string) => {
        const newSort = handleSortPagination(state.sortColumn, column);
        setState(prev => ({ ...prev, sortColumn: newSort, page: DEFAULT_PAGE }));
    };

    const handlePageChange = (page: number) => {
        setState(prev => ({ ...prev, page }));
    };

    const handleLimitChange = (limit: number) => {
        setState(prev => ({ ...prev, limit, page: DEFAULT_PAGE }));
    };

    const handleSearchInputChange = (value: string) => {
        setSearchInput(value);
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const openRemoveModalHandler = (obj: ObjectReference) => {
        setRemoveObject(obj);
    };

    const closeRemoveModal = () => {
        setRemoveObject(null);
    };

    const removeDoctor = async () => {
        if (!removeObject) return;

        setState(prev => ({ ...prev, loading: true }));

        try {
            await agent.DoctorProvider.remove(removeObject._id);
            fetchList();
        } catch (error) {
            console.error(`Ошибка удаления ${getDoctorTitle(DoctorTitleCase.a)}:`, error);
            setState(prev => ({
                ...prev,
                error: `Не удалось удалить ${getDoctorTitle(DoctorTitleCase.a)}. Пожалуйста, попробуйте позже.`,
            }));
        } finally {
            closeRemoveModal();
            setState(prev => ({ ...prev, loading: false }));
        }
    };

    const exportDoctorList = async () => {
        try {
            const task = await agent.DoctorProvider.exportDoctorList();
            setCreatedTask(task);
        } catch (error) {
            console.error(`Ошибка экспорта списка ${getDoctorTitle(DoctorTitleCase.ov)}:`, error);
        }
    };

    const closeExportModal = () => {
        setCreatedTask(undefined);
    };

    const getDeptNames = (deptIds?: string[]): string => {
        if (!deptIds || deptIds.length === 0) return "";
        return depts
            .filter(dept => deptIds.includes(dept._id.toString()))
            .map(dept => dept.name)
            .join("; ");
    };

    const {
        docs,
        totalDocs,
        totalPages,
        loading,
        error,
        page,
        limit,
        sortColumn,
    } = state;

    const styles = { ...commonStyles, ...localStyles };
    const headerLabel = `Список ${getDoctorTitle(DoctorTitleCase.ov)}`;
    const confirmRemoveLabel = `Вы уверены, что хотите удалить ${getDoctorTitle(DoctorTitleCase.a)}`;
    const createLabel = textLabels.createButton + ` ${getDoctorTitle(DoctorTitleCase.a)}`;
    const listLoaderLabel = textLabels.listLoaderMessage;
    const errorLabel = textLabels.errorMessage;

    return (
        <div className={styles.table}>
            <ConfirmDeleteModal
                items={removeObject ? [removeObject] : []}
                confirmLabel={confirmRemoveLabel}
                onConfirm={removeDoctor}
                onClose={closeRemoveModal}
            />

            {createdTask && (
                <CreatedTaskDetailsModal
                    task={createdTask}
                    onClose={closeExportModal}
                />
            )}

            <div className={styles.header}>
                <Header>{headerLabel}</Header>
                {hasRole(UserRole.ADMIN) && (
                    <Link to={`${currentPath}/create`}>
                        <Button
                            className={styles.createButton}
                            size="large"
                            color="green"
                            type="submit"
                        >
                            {createLabel}
                        </Button>
                    </Link>
                )}
            </div>

            {hasRole(UserRole.ADMIN) && (
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "10px" }}>
                    <Button
                        inverted
                        className={styles.createButton}
                        size="medium"
                        color="green"
                        onClick={exportDoctorList}
                    >
                        Экспорт списка {getDoctorTitle(DoctorTitleCase.ov)}
                    </Button>
                </div>
            )}

            {error && (
                <Message negative>
                    <Message.Header>{errorLabel}</Message.Header>
                    <p>{error}</p>
                </Message>
            )}

            {(loading || loadingDepts) ? (
                <Loader active>{listLoaderLabel}</Loader>
            ) : (
                <div className={styles.list}>
                    <Form>
                        <Form.Group>
                            <Form.Input
                                placeholder="ФИО"
                                iconPosition="left"
                                icon={<Icon name="search" />}
                                value={searchInput}
                                onChange={(e) => handleSearchInputChange(e.target.value)}
                                onKeyPress={handleKeyPress}
                            />
                            <Form.Button primary content="Поиск" onClick={handleSearch} />
                            <Form.Button basic color="orange" content="Сбросить" onClick={handleClearSearch} />
                        </Form.Group>
                    </Form>

                    <div className={styles.container}>
                        <Table celled striped selectable verticalAlign="middle" color="teal">
                            <Table.Header>
                                <Table.Row>
                                    <Table.HeaderCell
                                        sorted={
                                            sortColumn === "surname" || sortColumn === "-surname"
                                                ? sortColumn.startsWith("-")
                                                    ? "descending"
                                                    : "ascending"
                                                : undefined
                                        }
                                        onClick={() => handleSort("surname")}
                                    >
                                        ФИО
                                    </Table.HeaderCell>
                                    <Table.HeaderCell
                                        sorted={
                                            sortColumn === "deptName" || sortColumn === "-deptName"
                                                ? sortColumn.startsWith("-")
                                                    ? "descending"
                                                    : "ascending"
                                                : undefined
                                        }
                                        onClick={() => handleSort("deptName")}
                                    >
                                        {firstLetterToUpperCase(getGeneralTitle(GeneralTitleCase.departmantim))}
                                    </Table.HeaderCell>
                                    <Table.HeaderCell
                                        sorted={
                                            sortColumn === "tel" || sortColumn === "-tel"
                                                ? sortColumn.startsWith("-")
                                                    ? "descending"
                                                    : "ascending"
                                                : undefined
                                        }
                                        onClick={() => handleSort("tel")}
                                    >
                                        Телефон
                                    </Table.HeaderCell>
                                    {hasRole(UserRole.ADMIN) && <Table.HeaderCell />}
                                </Table.Row>
                            </Table.Header>

                            <Table.Body>
                                {docs.map((el) => (
                                    <Table.Row style={{ cursor: "pointer" }} key={`doctor-${el._id}`}>
                                        <Table.Cell floated="left">
                                            {hasRole(UserRole.ADMIN) ? (
                                                <Link
                                                    className={styles.nameColumn}
                                                    to={{
                                                        pathname: `${currentPath}/${el._id}/edit`,
                                                        search: `?returnUrl=${encodeURIComponent(window.location.href)}`,
                                                    }}
                                                >
                                                    {`${el.surname} ${el.name} ${el.middlename}`}
                                                </Link>
                                            ) : (
                                                `${el.surname} ${el.name} ${el.middlename}`
                                            )}
                                        </Table.Cell>

                                        <Table.Cell style={{ width: "25%" }} floated="left">
                                            {hasRole(UserRole.ADMIN) ? (
                                                <Link
                                                    className={styles.linkCell}
                                                    to={{
                                                        pathname: `${currentPath}/${el._id}/edit`,
                                                        search: `?returnUrl=${encodeURIComponent(window.location.href)}`,
                                                    }}
                                                >
                                                    {getDeptNames(el.deptIds)}
                                                </Link>
                                            ) : (
                                                getDeptNames(el.deptIds)
                                            )}
                                        </Table.Cell>

                                        <Table.Cell style={{ width: "15%" }} floated="left">
                                            {hasRole(UserRole.ADMIN) ? (
                                                <Link
                                                    className={styles.linkCell}
                                                    to={{
                                                        pathname: `${currentPath}/${el._id}/edit`,
                                                        search: `?returnUrl=${encodeURIComponent(window.location.href)}`,
                                                    }}
                                                >
                                                    {el.tel || ""}
                                                </Link>
                                            ) : (
                                                el.tel || ""
                                            )}
                                        </Table.Cell>

                                        {hasRole(UserRole.ADMIN) && (
                                            <Table.Cell floated="right" style={{ width: "5%" }}>
                                                <Button
                                                    icon
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        openRemoveModalHandler({ _id: el._id, name: Utils.getFullName(el.surname, el.name, el.middlename) });
                                                    }}
                                                >
                                                    <Icon color="red" name="trash alternate outline" />
                                                </Button>
                                            </Table.Cell>
                                        )}
                                    </Table.Row>
                                ))}
                            </Table.Body>

                            <Table.Footer>
                                <Table.Row>
                                    <Table.HeaderCell colSpan={hasRole(UserRole.ADMIN) ? 4 : 3}>
                                        <Menu floated="right" pagination>
                                            <TablePage
                                                currentPage={page}
                                                totalPages={totalPages}
                                                totalDocs={totalDocs}
                                                limit={limit}
                                                onPageChange={handlePageChange}
                                                onLimitChange={handleLimitChange}
                                            />
                                        </Menu>
                                    </Table.HeaderCell>
                                </Table.Row>
                            </Table.Footer>
                        </Table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DoctorsList;